import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { ReactElement, useState } from 'react'
import { MagnifierIcon } from '@/common/icons/general.tsx'
import { IconType } from '@/common/icons/types.ts'

type AdvancedSelectProps<T> = {
  options: T[]
  displayValue: (option: T | null) => string
  onChange: (option: T | null) => void
  placeholder?: string
  embeddedIcon?: ReactElement
  handleFocus?: () => void
}

export default function AdvancedSelect<T extends { id: number }>({
  options,
  displayValue,
  onChange,
  placeholder = 'Search...',
  embeddedIcon = <MagnifierIcon type={IconType.Outline} />,
  handleFocus,
}: AdvancedSelectProps<T>) {
  const [query, setQuery] = useState('')
  const [selectedOption, setSelectedOption] = useState<T | null>(null)

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) => {
          return displayValue(option)
            .toLowerCase()
            .includes(query.toLowerCase())
        })

  const handleOptionChange = (option: T | null) => {
    setQuery('')
    setSelectedOption(option)
    onChange(option) // Notify the parent component
  }

  const handleFocusProxy = () => {
    if (handleFocus) handleFocus()
  }

  return (
    <Combobox as="div" value={selectedOption} onChange={handleOptionChange}>
      <div className="relative">
        <ComboboxInput
          className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
          onChange={(event) => setQuery(event.target.value)}
          onBlur={() => setQuery('')}
          placeholder={placeholder}
          displayValue={displayValue}
          onFocus={handleFocusProxy}
        />
        <ComboboxButton
          className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
          onFocus={handleFocusProxy}
        >
          {embeddedIcon}
        </ComboboxButton>

        {filteredOptions.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.map((option) => (
              <ComboboxOption
                key={option.id}
                value={option}
                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white"
              >
                <span className="block truncate group-data-[selected]:font-semibold">
                  {displayValue(option)}
                </span>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary-600 group-data-[selected]:flex group-data-[focus]:text-white">
                  <i className="ki-filled ki-check"></i>
                </span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  )
}
